import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  LuBriefcase,
  LuCalendarClock,
  LuCircleCheck,
  LuClapperboard,
  LuClipboardList,
  LuExternalLink,
  LuFile,
  LuFileText,
  LuFolderOpen,
  LuPresentation,
  LuScale,
  LuSearchX,
  LuSparkles,
  LuTimerOff,
  LuVideo,
} from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import SegmentedControl from '../../../components/ui/SegmentedControl';
import { useTranslation } from '../../../i18n/LanguageContext';
import { EmptyState, HelpNote, LoadingRows, ProgressBar, SearchField } from '../components/ProfileUi';
import AssignedDocumentList from './AssignedDocumentList';
import DocumentRow, { KIND_TONES } from './DocumentRow';
import { sopKind } from './useDocumentLibrary';

const KIND_ICONS = { pdf: LuFileText, video: LuVideo, ppt: LuPresentation, other: LuFile };

const CATEGORIES = [
  { key: 'sop', icon: LuFileText, tone: 'tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30' },
  { key: 'education', icon: LuClapperboard, tone: 'tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30' },
  { key: 'forms', icon: LuClipboardList, tone: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-sky-500/30' },
  { key: 'policies', icon: LuScale, tone: 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30' },
  { key: 'jobDescriptions', icon: LuBriefcase, tone: 'tw-from-rose-400 tw-to-pink-600 tw-shadow-rose-500/30' },
];

const matches = (text, term) => String(text || '').toLocaleLowerCase('tr').includes(term);

/**
 * "Dosyalar": SOPs, trainings, forms, policies and job descriptions assigned
 * to the user's role. One category is open at a time; categories with new
 * documents say so on their card.
 */
const FilesSection = ({ library, assignedDocs }) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.files.${key}`, vars);
  const { counts } = library;
  // SOP listesi açık başlar; yeni form varsa ve yeni SOP yoksa formlar açılır
  const [openSection, setOpenSection] = useState('sop');
  const [search, setSearch] = useState('');
  const [sopFilter, setSopFilter] = useState('all');
  const listRef = useRef(null);
  const userToggled = useRef(false);

  useEffect(() => {
    if (userToggled.current) return;
    if (counts.newSops > 0) setOpenSection('sop');
    else if (counts.newForms > 0) setOpenSection('forms');
  }, [counts.newSops, counts.newForms]);

  const toggleSection = (key) => {
    userToggled.current = true;
    const next = openSection === key ? null : key;
    setOpenSection(next);
    setSearch('');
    if (next) {
      // Açılan liste görünür hale getirilir (yapışkan başlıkların altında kalmasın)
      window.requestAnimationFrame(() => {
        listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        try {
          listRef.current?.focus({ preventScroll: true });
        } catch {
          listRef.current?.focus();
        }
      });
    }
  };

  const term = search.trim().toLocaleLowerCase('tr');

  const sops = useMemo(() => library.mainSOPList
    .filter((sop) => matches(sop.name, term))
    .filter((sop) => {
      if (sopFilter === 'unread') return !library.readSOPs.has(sop.id);
      if (sopFilter === 'read') return library.readSOPs.has(sop.id);
      return true;
    })
    // Yeniler önce, sonra ada göre
    .sort((a, b) => {
      const newA = a.newlyAdded && !library.viewedSOPs.has(a.id) ? 0 : 1;
      const newB = b.newlyAdded && !library.viewedSOPs.has(b.id) ? 0 : 1;
      return newA - newB || String(a.name || '').localeCompare(String(b.name || ''), 'tr');
    }), [library.mainSOPList, library.readSOPs, library.viewedSOPs, term, sopFilter]);

  const educations = useMemo(
    () => library.indicationEducations.filter((education) => matches(education.title, term)),
    [library.indicationEducations, term]
  );

  // Form adları kodu ve başlığı birlikte içerir ("MT.FR.01 Masraf ... Formu")
  const forms = useMemo(
    () => library.formsList.filter((form) => matches(`${form.name || ''} ${form.versionNumber || ''}`, term)),
    [library.formsList, term]
  );

  // Politika / görev tanımı kartı: SOP kartı gibi okunan / toplam
  const assignedStat = (kind) => {
    const total = assignedDocs[kind].length;
    if (assignedDocs.loading) return { line: '…' };
    if (total === 0) return { line: label('cards.count', { count: 0 }) };
    const read = assignedDocs.readCounts[kind];
    return { line: label('cards.sopStat', { read, total }), percent: (read / total) * 100 };
  };

  const cardStats = {
    sop: {
      line: label('cards.sopStat', { read: counts.readSops, total: library.mainSOPList.length }),
      percent: library.mainSOPList.length ? (counts.readSops / library.mainSOPList.length) * 100 : 0,
      fresh: counts.newSops,
    },
    education: {
      line: label('cards.educationStat', { done: counts.watchedEducations, total: library.indicationEducations.length }),
      percent: library.indicationEducations.length ? (counts.watchedEducations / library.indicationEducations.length) * 100 : 0,
      fresh: counts.newEducations,
    },
    forms: { line: label('cards.count', { count: library.formsList.length }), fresh: counts.newForms },
    policies: assignedStat('policies'),
    jobDescriptions: assignedStat('jobDescriptions'),
  };

  const noResults = (emptyKey) => (
    <EmptyState
      compact
      icon={term ? LuSearchX : LuFolderOpen}
      title={term ? label('noMatch', { term: search.trim() }) : label(emptyKey)}
    />
  );

  const renderSops = () => {
    if (library.loading) return <LoadingRows count={4} />;
    if (sops.length === 0) return noResults('empty.sop');
    return (
      <ul className="tw-space-y-2">
        {sops.map((sop) => {
          const kind = sopKind(sop.fileType);
          const isNew = sop.newlyAdded && !library.viewedSOPs.has(sop.id);
          const isRead = library.readSOPs.has(sop.id);
          return (
            <DocumentRow
              key={sop.id}
              icon={KIND_ICONS[kind]}
              tone={KIND_TONES[kind]}
              title={sop.name}
              emphasis={isNew}
              onClick={() => library.openSOP(sop)}
              meta={(
                <>
                  {sop.versionNumber && <span className="tw-inline">v{sop.versionNumber}</span>}
                  <span className="tw-inline">{label(`kinds.${kind}`)}</span>
                </>
              )}
              badges={(
                <>
                  {isNew && <Badge tone="warning"><LuSparkles aria-hidden="true" className="tw-h-3 tw-w-3" />{label('badges.new')}</Badge>}
                  {isRead
                    ? <Badge tone="success"><LuCircleCheck aria-hidden="true" className="tw-h-3 tw-w-3" />{label('badges.read')}</Badge>
                    : <Badge tone="neutral">{label('badges.unread')}</Badge>}
                </>
              )}
            />
          );
        })}
      </ul>
    );
  };

  const renderEducations = () => {
    if (library.loading) return <LoadingRows count={3} />;
    if (educations.length === 0) return noResults('empty.education');
    const now = new Date();
    return (
      <ul className="tw-space-y-2">
        {educations.map((education) => {
          const deadline = education.hasDeadline && education.deadline ? new Date(education.deadline) : null;
          const isExpired = Boolean(deadline) && deadline < now;
          const daysLeft = deadline ? Math.ceil((deadline - now) / (1000 * 60 * 60 * 24)) : null;
          const done = library.viewedIndicationEducations.has(education.id);
          const isNew = education.newlyAdded && !done;
          let deadlineTone = 'tw-text-sky-700 dark:tw-text-sky-300';
          if (daysLeft !== null && daysLeft <= 3) deadlineTone = 'tw-text-rose-600 dark:tw-text-rose-300';
          else if (daysLeft !== null && daysLeft <= 7) deadlineTone = 'tw-text-amber-600 dark:tw-text-amber-300';
          return (
            <DocumentRow
              key={education.id}
              icon={LuVideo}
              tone={KIND_TONES.video}
              title={education.title}
              disabled={isExpired}
              emphasis={isNew && !isExpired}
              onClick={() => !isExpired && library.openIndicationEducation(education)}
              meta={(
                <>
                  {education.estimatedDuration && <span className="tw-inline">{education.estimatedDuration}</span>}
                  {deadline && !isExpired && (
                    <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-font-medium ${deadlineTone}`}>
                      <LuCalendarClock aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                      {label('deadline', { date: deadline.toLocaleDateString(locale) })}
                    </span>
                  )}
                </>
              )}
              badges={(
                <>
                  {isExpired && <Badge tone="danger"><LuTimerOff aria-hidden="true" className="tw-h-3 tw-w-3" />{label('badges.expired')}</Badge>}
                  {!isExpired && done && <Badge tone="success"><LuCircleCheck aria-hidden="true" className="tw-h-3 tw-w-3" />{label('badges.completed')}</Badge>}
                  {!isExpired && isNew && <Badge tone="warning"><LuSparkles aria-hidden="true" className="tw-h-3 tw-w-3" />{label('badges.new')}</Badge>}
                </>
              )}
            />
          );
        })}
      </ul>
    );
  };

  const renderForms = () => {
    if (library.loading) return <LoadingRows count={4} />;
    if (forms.length === 0) return noResults('empty.forms');
    return (
      <ul className="tw-space-y-2">
        {forms.map((form) => {
          const isNew = form.newlyAdded && !library.viewedForms.has(form.id);
          return (
            <DocumentRow
              key={form.id}
              icon={LuClipboardList}
              tone={KIND_TONES.form}
              title={form.name}
              emphasis={isNew}
              trailingIcon={LuExternalLink}
              onClick={() => library.openForm(form)}
              meta={form.versionNumber ? <span className="tw-inline">v{form.versionNumber}</span> : null}
              badges={isNew ? <Badge tone="warning"><LuSparkles aria-hidden="true" className="tw-h-3 tw-w-3" />{label('badges.new')}</Badge> : null}
            />
          );
        })}
      </ul>
    );
  };

  const searchPlaceholder = openSection ? label(`search.${openSection}`) : '';

  return (
    <div className="tw-space-y-5">
      {/* Categories */}
      <div className="tw-grid tw-grid-cols-2 tw-gap-2.5 md:tw-grid-cols-3 xl:tw-grid-cols-5">
        {CATEGORIES.map((category) => {
          const Icon = category.icon;
          const active = openSection === category.key;
          const stat = cardStats[category.key];
          return (
            <button
              key={category.key}
              type="button"
              onClick={() => toggleSection(category.key)}
              aria-expanded={active}
              aria-controls="profile-files-list"
              className={`tw-relative tw-flex tw-min-w-0 tw-flex-col tw-gap-3 tw-rounded-2xl tw-border tw-p-3.5 tw-text-left tw-transition-[background-color,border-color,box-shadow] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 sm:tw-p-4 ${
                active
                  ? 'tw-border-teal-500/50 tw-bg-teal-500/[0.07] tw-shadow-[0_10px_30px_-18px_rgba(13,148,136,0.7)] dark:tw-border-teal-400/40 dark:tw-bg-teal-400/[0.08]'
                  : 'tw-border-slate-200 tw-bg-white/70 hover:tw-bg-white dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:hover:tw-bg-white/[0.06]'
              }`}
            >
              <span className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                <span className={`tw-grid tw-h-10 tw-w-10 tw-place-items-center tw-rounded-xl tw-bg-gradient-to-br tw-text-white tw-shadow-lg ${category.tone}`}>
                  <Icon aria-hidden="true" className="tw-h-5 tw-w-5" />
                </span>
                {stat.fresh > 0 && (
                  <span className="tw-rounded-full tw-bg-amber-400 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-bold tw-text-amber-950">
                    {label('cards.new', { count: stat.fresh })}
                  </span>
                )}
              </span>
              <span className="tw-min-w-0 tw-space-y-1">
                <span className="tw-block tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{label(`categories.${category.key}.title`)}</span>
                <span className="tw-hidden tw-text-xs tw-leading-snug tw-text-slate-500 sm:tw-block dark:tw-text-slate-400">{label(`categories.${category.key}.description`)}</span>
              </span>
              <span className="tw-mt-auto tw-space-y-1.5">
                <span className="tw-block tw-text-xs tw-font-medium tw-tabular-nums tw-text-slate-600 dark:tw-text-slate-300">{stat.line}</span>
                {stat.percent !== undefined && <ProgressBar percent={stat.percent} />}
              </span>
            </button>
          );
        })}
      </div>

      {openSection && (
        <section
          id="profile-files-list"
          ref={listRef}
          tabIndex={-1}
          aria-label={label(`categories.${openSection}.title`)}
          className="tw-scroll-mt-52 tw-space-y-3 focus:tw-outline-none"
        >
          <div className="tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
            <h3 className="tw-text-base tw-font-semibold tw-text-slate-900 dark:tw-text-white">{label(`categories.${openSection}.title`)}</h3>
            {openSection === 'sop' && (
              <SegmentedControl
                size="sm"
                layoutId="profile-sop-filter"
                label={label('sopFilter.label')}
                value={sopFilter}
                onChange={setSopFilter}
                options={[
                  { value: 'all', label: label('sopFilter.all') },
                  { value: 'unread', label: label('sopFilter.unread', { count: counts.unreadSops }) },
                  { value: 'read', label: label('sopFilter.read', { count: counts.readSops }) },
                ]}
              />
            )}
          </div>

          {openSection === 'sop' && <HelpNote>{label('help.sop')}</HelpNote>}
          {openSection === 'education' && <HelpNote>{label('help.education')}</HelpNote>}
          {openSection === 'forms' && <HelpNote>{label('help.forms')}</HelpNote>}

          {(openSection === 'sop' || openSection === 'education' || openSection === 'forms') && (
            <SearchField value={search} onChange={setSearch} placeholder={searchPlaceholder} />
          )}

          {openSection === 'sop' && renderSops()}
          {openSection === 'education' && renderEducations()}
          {openSection === 'forms' && renderForms()}
          {(openSection === 'policies' || openSection === 'jobDescriptions') && (
            <>
              <HelpNote>{label('help.assigned')}</HelpNote>
              <AssignedDocumentList
                key={openSection}
                kind={openSection}
                documents={assignedDocs[openSection]}
                loading={assignedDocs.loading}
                error={assignedDocs.error}
                isRead={(docId) => assignedDocs.isRead(openSection, docId)}
                onToggleRead={(docId) => assignedDocs.toggleRead(openSection, docId)}
              />
            </>
          )}
        </section>
      )}
    </div>
  );
};

FilesSection.propTypes = {
  library: PropTypes.object.isRequired,
  assignedDocs: PropTypes.shape({
    policies: PropTypes.array.isRequired,
    jobDescriptions: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    error: PropTypes.string,
    isRead: PropTypes.func.isRequired,
    toggleRead: PropTypes.func.isRequired,
    readCounts: PropTypes.objectOf(PropTypes.number).isRequired,
  }).isRequired,
};

export default FilesSection;
